/**
 * AUTO-GENERATED — DO NOT EDIT
 *
 * Pure type definitions for endpoint errors.
 * No runtime imports — safe to import from any module without circular deps.
 * Run `npm run monsterrat:generate:contracts` to regenerate.
 */

import type { ActiveMessagesResponse, AnalyzeResponse, AudioEpisodeByLessonResponse, AudioEpisodeDetailResponse, AudioProgressListResponse, AudioProgressResponse, AudioSeriesDetailResponse, AudioSeriesListResponse, CancelDeletionResponse, CheckLessonUpdatesResponse, CompetenceMapResponse, ConfigResponse, CoursesResponse, DeletionStatusResponse, ExerciseGenerationResponse, ExpressionItemDetailResponse, ExpressionItemListResponse, GenerateLessonResponse, HealthResponse, LessonCompleteResponse, LessonDetailResponse, LessonDraftResponse, LessonHistoryResponse, LessonIndexResponse, LessonListResponse, LessonPathResponse, LessonRecommendedResponse, LessonSearchResponse, MyReportsResponse, QuickPracticeResponse, ReportCreateResponse, RequestDeletionResponse, RolePlayCollectionDetailResponse, RolePlayCollectionListResponse, RolePlaySlotDetailResponse, RolePlaySlotListResponse, RolePlayTemplateDetailResponse, RolePlayTemplateListResponse, ScheduledLessonsResponse, SessionListResponse, SessionMessagesResponse, SessionSummary, SoundMapResponse, Subscription, SuccessEnvelope, TargetCapabilitiesResponse, UserProfile, VocabItemDetailResponse, VocabItemListResponse, VocabSetDetailResponse, VocabSetMarkResponse, VocabSetsResponse, VocabSummaryResponse } from './http-contracts';

// ══════════════════════════════════════════════════════════════
// Transport Errors (shared across all endpoints)
// ══════════════════════════════════════════════════════════════

export type TransportError =
  | { code: 'OFFLINE'; retryable: true }
  | { code: 'TIMEOUT'; retryable: true }
  | { code: 'SERVER_UNREACHABLE'; retryable: true }
  | { code: 'SERVER_ERROR'; status: number; retryable: true; referenceId?: string }
  | { code: 'UNKNOWN'; retryable: false };

// ══════════════════════════════════════════════════════════════
// Domain Error Base
// ══════════════════════════════════════════════════════════════

/** Common fields on all domain errors */
export interface DomainErrorBase {
  message: string;
}

export type ErrorCategory = 'VALIDATION' | 'BUSINESS' | 'AUTH' | 'UNEXPECTED';

// ══════════════════════════════════════════════════════════════
// Implicit Error Sets (derived from middleware-errors.yaml)
// ══════════════════════════════════════════════════════════════

export type AuthErrors =
  | { code: 'UNAUTHORIZED'; category: 'AUTH' }
  | { code: 'TOKEN_EXPIRED'; category: 'AUTH' }
  | { code: 'ACCOUNT_DELETED'; category: 'AUTH' };

export type RequireAdminErrors =
  | { code: 'FORBIDDEN'; category: 'AUTH' };

export type ConsentErrors =
  | { code: 'AI_CONSENT_REQUIRED'; category: 'AUTH' };

export type RateLimitErrors =
  | { code: 'RATE_LIMITED'; category: 'BUSINESS'; retryAfter?: number };

export type ValidationErrors =
  | { code: 'VALIDATION_FAILED'; category: 'VALIDATION'; fieldErrors?: Record<string, string[]> };

export type UniversalErrors =
  | { code: 'INTERNAL_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | { code: 'SERVICE_UNAVAILABLE'; category: 'UNEXPECTED'; referenceId?: string };

// ══════════════════════════════════════════════════════════════
// Per-Endpoint Domain Error Unions
// ══════════════════════════════════════════════════════════════

/** GET /api/health */
export type CheckHealthDomainError =
  | UniversalErrors;

/** GET /api/config */
export type GetConfigDomainError =
  | UniversalErrors;

/** GET /api/user/me */
export type GetUserProfileDomainError =
  | { code: 'USER_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'ACCOUNT_DELETED'; category: 'AUTH' }
  | AuthErrors
  | UniversalErrors;

/** POST /api/user/sync */
export type SyncUserDomainError =
  | { code: 'USER_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'ACCOUNT_DELETED'; category: 'AUTH' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** PATCH /api/user/preferences */
export type UpdatePreferencesDomainError =
  | { code: 'USER_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'ACCOUNT_DELETED'; category: 'AUTH' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** PATCH /api/user/course-preferences */
export type UpdateCoursePreferenceDomainError =
  | { code: 'USER_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'ACCOUNT_DELETED'; category: 'AUTH' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/lessons */
export type ListLessonsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/lessons/recommended */
export type GetRecommendedLessonsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/lessons/index */
export type GetLessonIndexDomainError =
  | AuthErrors
  | UniversalErrors;

/** GET /api/lessons/path */
export type GetLessonPathDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/lessons/scheduled */
export type GetScheduledLessonsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/check-updates */
export type CheckLessonUpdatesDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/lessons/:slug */
export type GetLessonDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/lessons/:slug/history */
export type GetLessonHistoryDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/lessons/draft/:id */
export type GetLessonDraftDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** POST /api/lessons/search */
export type SearchLessonsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/ai-search */
export type AiSearchLessonsDomainError =
  | { code: 'AI_SERVICE_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | AuthErrors
  | RateLimitErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/generate */
export type GenerateLessonDomainError =
  | { code: 'AI_SERVICE_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | AuthErrors
  | ConsentErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/:slug/generate-exercises */
export type GenerateExercisesDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'AI_SERVICE_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | AuthErrors
  | ConsentErrors
  | RateLimitErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/:slug/complete */
export type CompleteLessonAttemptDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/quick-practice */
export type GenerateQuickPracticeDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'AI_SERVICE_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | AuthErrors
  | ConsentErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/lessons/feedback */
export type SubmitLessonFeedbackDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/conversation/sessions */
export type CreateSessionDomainError =
  | { code: 'GRAMMAR_POINT_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ConsentErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/conversation/sessions */
export type ListSessionsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/conversation/sessions/:id/participants */
export type AddSessionParticipantDomainError =
  | { code: 'FORBIDDEN'; category: 'AUTH' }
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/conversation/sessions/:id/messages */
export type GetSessionMessagesDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** POST /api/conversation/sessions/:id/messages/:messageId/analyze */
export type AnalyzeMessageDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'AI_SERVICE_ERROR'; category: 'UNEXPECTED'; referenceId?: string }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/roleplay/slots */
export type ListRolePlaySlotsDomainError =
  | AuthErrors
  | UniversalErrors;

/** GET /api/roleplay/slots/:id */
export type GetRolePlaySlotDomainError =
  | { code: 'ROLEPLAY_SLOT_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** POST /api/roleplay/slots/:id/restart */
export type RestartRolePlaySlotDomainError =
  | { code: 'ROLEPLAY_SLOT_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ConsentErrors
  | UniversalErrors;

/** DELETE /api/roleplay/slots/:id */
export type DeleteRolePlaySlotDomainError =
  | { code: 'ROLEPLAY_SLOT_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/roleplay/templates */
export type ListRolePlayTemplatesDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/roleplay/templates/:id */
export type GetRolePlayTemplateDomainError =
  | { code: 'ROLEPLAY_TEMPLATE_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/roleplay/templates/:id/start */
export type StartRolePlayTemplateDomainError =
  | { code: 'ROLEPLAY_TEMPLATE_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'ROLEPLAY_SLOTS_FULL'; category: 'BUSINESS' }
  | { code: 'ROLEPLAY_SETUP_INVALID'; category: 'VALIDATION' }
  | AuthErrors
  | ConsentErrors
  | UniversalErrors;

/** GET /api/roleplay/collections */
export type ListRolePlayCollectionsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/roleplay/collections/:slug */
export type GetRolePlayCollectionDomainError =
  | { code: 'ROLEPLAY_COLLECTION_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/roleplay/templates/search */
export type SearchRolePlayTemplatesDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/items */
export type ListVocabItemsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/items/:id */
export type GetVocabItemDomainError =
  | { code: 'VOCAB_ITEM_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/expressions */
export type ListExpressionItemsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/expressions/:id */
export type GetExpressionItemDomainError =
  | { code: 'EXPRESSION_ITEM_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/summary */
export type GetVocabSummaryDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/sets */
export type GetVocabSetsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/vocab/sets/:slug */
export type GetVocabSetDomainError =
  | { code: 'VOCAB_SET_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/vocab/sets/:slug/mark */
export type MarkVocabSetItemDomainError =
  | { code: 'VOCAB_SET_NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'VOCAB_ITEM_NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/account/deletion-status */
export type GetDeletionStatusDomainError =
  | AuthErrors
  | UniversalErrors;

/** POST /api/account/request-deletion */
export type RequestDeletionDomainError =
  | { code: 'CONFLICT'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/account/cancel-deletion */
export type CancelDeletionDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/subscription/status */
export type GetSubscriptionStatusDomainError =
  | AuthErrors
  | UniversalErrors;

/** POST /api/subscription/refresh */
export type RefreshSubscriptionDomainError =
  | AuthErrors
  | UniversalErrors;

/** GET /api/speech/sound-map */
export type GetSoundMapDomainError =
  | AuthErrors
  | RateLimitErrors
  | UniversalErrors;

/** GET /api/grammar/competence-map */
export type GetCompetenceMapDomainError =
  | AuthErrors
  | RateLimitErrors
  | UniversalErrors;

/** POST /api/reports/ai-content */
export type ReportAIContentDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/reports/ai-content/my-reports */
export type GetMyReportsDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/messages/active */
export type GetActiveMessagesDomainError =
  | AuthErrors
  | UniversalErrors;

/** POST /api/messages/:id/dismiss */
export type DismissMessageDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/courses */
export type GetCoursesDomainError =
  | AuthErrors
  | UniversalErrors;

/** GET /api/courses/capabilities */
export type GetTargetCapabilitiesDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/audio/series */
export type ListAudioSeriesDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/audio/series/:slug */
export type GetAudioSeriesDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | UniversalErrors;

/** GET /api/audio/progress */
export type ListAudioProgressDomainError =
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** GET /api/audio/episodes/by-lesson/:lessonSlug */
export type GetAudioEpisodeByLessonDomainError =
  | AuthErrors
  | UniversalErrors;

/** GET /api/audio/episodes/:slug */
export type GetAudioEpisodeDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | { code: 'FORBIDDEN'; category: 'AUTH' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

/** POST /api/audio/episodes/:slug/progress */
export type SaveAudioProgressDomainError =
  | { code: 'NOT_FOUND'; category: 'BUSINESS' }
  | AuthErrors
  | ValidationErrors
  | UniversalErrors;

// ══════════════════════════════════════════════════════════════
// Per-Endpoint Result Types
// ══════════════════════════════════════════════════════════════

/** Result for GET /api/health */
export type CheckHealthResult =
  | { ok: true; data: HealthResponse }
  | { ok: false; kind: 'domain'; error: CheckHealthDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/config */
export type GetConfigResult =
  | { ok: true; data: ConfigResponse }
  | { ok: false; kind: 'domain'; error: GetConfigDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/user/me */
export type GetUserProfileResult =
  | { ok: true; data: UserProfile }
  | { ok: false; kind: 'domain'; error: GetUserProfileDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/user/sync */
export type SyncUserResult =
  | { ok: true; data: UserProfile }
  | { ok: false; kind: 'domain'; error: SyncUserDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for PATCH /api/user/preferences */
export type UpdatePreferencesResult =
  | { ok: true; data: UserProfile }
  | { ok: false; kind: 'domain'; error: UpdatePreferencesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for PATCH /api/user/course-preferences */
export type UpdateCoursePreferenceResult =
  | { ok: true; data: UserProfile }
  | { ok: false; kind: 'domain'; error: UpdateCoursePreferenceDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons */
export type ListLessonsResult =
  | { ok: true; data: LessonListResponse }
  | { ok: false; kind: 'domain'; error: ListLessonsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/recommended */
export type GetRecommendedLessonsResult =
  | { ok: true; data: LessonRecommendedResponse }
  | { ok: false; kind: 'domain'; error: GetRecommendedLessonsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/index */
export type GetLessonIndexResult =
  | { ok: true; data: LessonIndexResponse }
  | { ok: false; kind: 'domain'; error: GetLessonIndexDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/path */
export type GetLessonPathResult =
  | { ok: true; data: LessonPathResponse }
  | { ok: false; kind: 'domain'; error: GetLessonPathDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/scheduled */
export type GetScheduledLessonsResult =
  | { ok: true; data: ScheduledLessonsResponse }
  | { ok: false; kind: 'domain'; error: GetScheduledLessonsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/check-updates */
export type CheckLessonUpdatesResult =
  | { ok: true; data: CheckLessonUpdatesResponse }
  | { ok: false; kind: 'domain'; error: CheckLessonUpdatesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/:slug */
export type GetLessonResult =
  | { ok: true; data: LessonDetailResponse }
  | { ok: false; kind: 'domain'; error: GetLessonDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/:slug/history */
export type GetLessonHistoryResult =
  | { ok: true; data: LessonHistoryResponse }
  | { ok: false; kind: 'domain'; error: GetLessonHistoryDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/lessons/draft/:id */
export type GetLessonDraftResult =
  | { ok: true; data: LessonDraftResponse }
  | { ok: false; kind: 'domain'; error: GetLessonDraftDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/search */
export type SearchLessonsResult =
  | { ok: true; data: LessonSearchResponse }
  | { ok: false; kind: 'domain'; error: SearchLessonsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/ai-search */
export type AiSearchLessonsResult =
  | { ok: true; data: LessonSearchResponse }
  | { ok: false; kind: 'domain'; error: AiSearchLessonsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/generate */
export type GenerateLessonResult =
  | { ok: true; data: GenerateLessonResponse }
  | { ok: false; kind: 'domain'; error: GenerateLessonDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/:slug/generate-exercises */
export type GenerateExercisesResult =
  | { ok: true; data: ExerciseGenerationResponse }
  | { ok: false; kind: 'domain'; error: GenerateExercisesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/:slug/complete */
export type CompleteLessonAttemptResult =
  | { ok: true; data: LessonCompleteResponse }
  | { ok: false; kind: 'domain'; error: CompleteLessonAttemptDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/quick-practice */
export type GenerateQuickPracticeResult =
  | { ok: true; data: QuickPracticeResponse }
  | { ok: false; kind: 'domain'; error: GenerateQuickPracticeDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/lessons/feedback */
export type SubmitLessonFeedbackResult =
  | { ok: true; data: SuccessEnvelope }
  | { ok: false; kind: 'domain'; error: SubmitLessonFeedbackDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/conversation/sessions */
export type CreateSessionResult =
  | { ok: true; data: SessionSummary }
  | { ok: false; kind: 'domain'; error: CreateSessionDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/conversation/sessions */
export type ListSessionsResult =
  | { ok: true; data: SessionListResponse }
  | { ok: false; kind: 'domain'; error: ListSessionsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/conversation/sessions/:id/participants */
export type AddSessionParticipantResult =
  | { ok: true; data: SuccessEnvelope }
  | { ok: false; kind: 'domain'; error: AddSessionParticipantDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/conversation/sessions/:id/messages */
export type GetSessionMessagesResult =
  | { ok: true; data: SessionMessagesResponse }
  | { ok: false; kind: 'domain'; error: GetSessionMessagesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/conversation/sessions/:id/messages/:messageId/analyze */
export type AnalyzeMessageResult =
  | { ok: true; data: AnalyzeResponse }
  | { ok: false; kind: 'domain'; error: AnalyzeMessageDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/slots */
export type ListRolePlaySlotsResult =
  | { ok: true; data: RolePlaySlotListResponse }
  | { ok: false; kind: 'domain'; error: ListRolePlaySlotsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/slots/:id */
export type GetRolePlaySlotResult =
  | { ok: true; data: RolePlaySlotDetailResponse }
  | { ok: false; kind: 'domain'; error: GetRolePlaySlotDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/roleplay/slots/:id/restart */
export type RestartRolePlaySlotResult =
  | { ok: true; data: RolePlaySlotDetailResponse }
  | { ok: false; kind: 'domain'; error: RestartRolePlaySlotDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for DELETE /api/roleplay/slots/:id */
export type DeleteRolePlaySlotResult =
  | { ok: true; data: SuccessEnvelope }
  | { ok: false; kind: 'domain'; error: DeleteRolePlaySlotDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/templates */
export type ListRolePlayTemplatesResult =
  | { ok: true; data: RolePlayTemplateListResponse }
  | { ok: false; kind: 'domain'; error: ListRolePlayTemplatesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/templates/:id */
export type GetRolePlayTemplateResult =
  | { ok: true; data: RolePlayTemplateDetailResponse }
  | { ok: false; kind: 'domain'; error: GetRolePlayTemplateDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/roleplay/templates/:id/start */
export type StartRolePlayTemplateResult =
  | { ok: true; data: RolePlaySlotDetailResponse }
  | { ok: false; kind: 'domain'; error: StartRolePlayTemplateDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/collections */
export type ListRolePlayCollectionsResult =
  | { ok: true; data: RolePlayCollectionListResponse }
  | { ok: false; kind: 'domain'; error: ListRolePlayCollectionsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/collections/:slug */
export type GetRolePlayCollectionResult =
  | { ok: true; data: RolePlayCollectionDetailResponse }
  | { ok: false; kind: 'domain'; error: GetRolePlayCollectionDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/roleplay/templates/search */
export type SearchRolePlayTemplatesResult =
  | { ok: true; data: RolePlayTemplateListResponse }
  | { ok: false; kind: 'domain'; error: SearchRolePlayTemplatesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/items */
export type ListVocabItemsResult =
  | { ok: true; data: VocabItemListResponse }
  | { ok: false; kind: 'domain'; error: ListVocabItemsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/items/:id */
export type GetVocabItemResult =
  | { ok: true; data: VocabItemDetailResponse }
  | { ok: false; kind: 'domain'; error: GetVocabItemDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/expressions */
export type ListExpressionItemsResult =
  | { ok: true; data: ExpressionItemListResponse }
  | { ok: false; kind: 'domain'; error: ListExpressionItemsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/expressions/:id */
export type GetExpressionItemResult =
  | { ok: true; data: ExpressionItemDetailResponse }
  | { ok: false; kind: 'domain'; error: GetExpressionItemDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/summary */
export type GetVocabSummaryResult =
  | { ok: true; data: VocabSummaryResponse }
  | { ok: false; kind: 'domain'; error: GetVocabSummaryDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/sets */
export type GetVocabSetsResult =
  | { ok: true; data: VocabSetsResponse }
  | { ok: false; kind: 'domain'; error: GetVocabSetsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/vocab/sets/:slug */
export type GetVocabSetResult =
  | { ok: true; data: VocabSetDetailResponse }
  | { ok: false; kind: 'domain'; error: GetVocabSetDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/vocab/sets/:slug/mark */
export type MarkVocabSetItemResult =
  | { ok: true; data: VocabSetMarkResponse }
  | { ok: false; kind: 'domain'; error: MarkVocabSetItemDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/account/deletion-status */
export type GetDeletionStatusResult =
  | { ok: true; data: DeletionStatusResponse }
  | { ok: false; kind: 'domain'; error: GetDeletionStatusDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/account/request-deletion */
export type RequestDeletionResult =
  | { ok: true; data: RequestDeletionResponse }
  | { ok: false; kind: 'domain'; error: RequestDeletionDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/account/cancel-deletion */
export type CancelDeletionResult =
  | { ok: true; data: CancelDeletionResponse }
  | { ok: false; kind: 'domain'; error: CancelDeletionDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/subscription/status */
export type GetSubscriptionStatusResult =
  | { ok: true; data: Subscription }
  | { ok: false; kind: 'domain'; error: GetSubscriptionStatusDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/subscription/refresh */
export type RefreshSubscriptionResult =
  | { ok: true; data: Subscription }
  | { ok: false; kind: 'domain'; error: RefreshSubscriptionDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/speech/sound-map */
export type GetSoundMapResult =
  | { ok: true; data: SoundMapResponse }
  | { ok: false; kind: 'domain'; error: GetSoundMapDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/grammar/competence-map */
export type GetCompetenceMapResult =
  | { ok: true; data: CompetenceMapResponse }
  | { ok: false; kind: 'domain'; error: GetCompetenceMapDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/reports/ai-content */
export type ReportAIContentResult =
  | { ok: true; data: ReportCreateResponse }
  | { ok: false; kind: 'domain'; error: ReportAIContentDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/reports/ai-content/my-reports */
export type GetMyReportsResult =
  | { ok: true; data: MyReportsResponse }
  | { ok: false; kind: 'domain'; error: GetMyReportsDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/messages/active */
export type GetActiveMessagesResult =
  | { ok: true; data: ActiveMessagesResponse }
  | { ok: false; kind: 'domain'; error: GetActiveMessagesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/messages/:id/dismiss */
export type DismissMessageResult =
  | { ok: true; data: SuccessEnvelope }
  | { ok: false; kind: 'domain'; error: DismissMessageDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/courses */
export type GetCoursesResult =
  | { ok: true; data: CoursesResponse }
  | { ok: false; kind: 'domain'; error: GetCoursesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/courses/capabilities */
export type GetTargetCapabilitiesResult =
  | { ok: true; data: TargetCapabilitiesResponse }
  | { ok: false; kind: 'domain'; error: GetTargetCapabilitiesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/audio/series */
export type ListAudioSeriesResult =
  | { ok: true; data: AudioSeriesListResponse }
  | { ok: false; kind: 'domain'; error: ListAudioSeriesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/audio/series/:slug */
export type GetAudioSeriesResult =
  | { ok: true; data: AudioSeriesDetailResponse }
  | { ok: false; kind: 'domain'; error: GetAudioSeriesDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/audio/progress */
export type ListAudioProgressResult =
  | { ok: true; data: AudioProgressListResponse }
  | { ok: false; kind: 'domain'; error: ListAudioProgressDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/audio/episodes/by-lesson/:lessonSlug */
export type GetAudioEpisodeByLessonResult =
  | { ok: true; data: AudioEpisodeByLessonResponse }
  | { ok: false; kind: 'domain'; error: GetAudioEpisodeByLessonDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for GET /api/audio/episodes/:slug */
export type GetAudioEpisodeResult =
  | { ok: true; data: AudioEpisodeDetailResponse }
  | { ok: false; kind: 'domain'; error: GetAudioEpisodeDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

/** Result for POST /api/audio/episodes/:slug/progress */
export type SaveAudioProgressResult =
  | { ok: true; data: AudioProgressResponse }
  | { ok: false; kind: 'domain'; error: SaveAudioProgressDomainError & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError };

// ══════════════════════════════════════════════════════════════
// Store Error State
// ══════════════════════════════════════════════════════════════

/** Discriminated error state for Zustand stores */
export type StoreErrorInfo =
  | { kind: 'domain'; error: DomainErrorBase & { code: string; category: ErrorCategory } }
  | { kind: 'transport'; error: TransportError };

